import React, { forwardRef, useImperativeHandle, useState } from "react";
import { Dialog } from "primereact/dialog";
import { DataView } from "primereact/dataview";
import MeaLoadingContent from "../../../../components/MeaLoadingContent";
import MeaMemberAvatar from "../../../../components/MeaMemberAvatar";
import contractsAPI from "../../../../services/contracts";
import { ToFormatedDateForEvents } from "../../../../utils/dates";

const FIELD_MAPPINGS = {
  status: "Statut",
  total_amount: "Montant total",
  remaining_amount: "Montant restant",
  payment_status: "Statut de paiement",
  start_date: "Date de début",
  end_date: "Date de fin",
  notes: "Notes",
  students: "Élèves",
  student_added: "Élève rajouté",
  parents: "Parents",
  student_removed: "Élève retiré",
  payment_amount: "Montant du paiement",
  payment_method: "Méthode de paiement",
  payment_date: "Date d'encaissement du paiement",
  payment_received_date: "Date de réception du paiement",
  payment_added: "Paiement ajouté",
  payment_removed: "Paiement supprimé",
  paid_balance: "Montant payé",
};

const PAYMENT_FIELD_PREFIXES = ["payment_"];
const PAYMENT_FIELD_NAMES = new Set(["paid_balance"]);

const formatValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "—";
  }

  if (typeof value === "boolean") {
    return value ? "Oui" : "Non";
  }

  return value;
};

const looksLikeDateTime = (fieldName, value) => {
  if (!value || typeof value !== "string") {
    return false;
  }

  if (fieldName && fieldName.toLowerCase().includes("date")) {
    return true;
  }

  const parsed = new Date(value);
  return !Number.isNaN(parsed.getTime());
};

const getDisplayValue = (fieldName, valueName, value) => {
  if (looksLikeDateTime(fieldName, value)) {
    return value;
  }

  if (valueName !== undefined && valueName !== null && valueName !== "") {
    return valueName;
  }
  return value;
};

const isPaymentField = (fieldName) => {
  if (!fieldName) {
    return false;
  }
  if (PAYMENT_FIELD_NAMES.has(fieldName)) {
    return true;
  }
  return PAYMENT_FIELD_PREFIXES.some((prefix) => fieldName.startsWith(prefix));
};

const formatDisplayValue = (fieldName, value) => {
  if (looksLikeDateTime(fieldName, value)) {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return ToFormatedDateForEvents(parsed, true);
    }
  }

  return formatValue(value);
};

const getFieldDisplayName = (fieldName) => {
  return FIELD_MAPPINGS[fieldName] || fieldName;
};

const parseChanges = (changes) => {
  if (!changes) {
    return [];
  }

  if (Array.isArray(changes)) {
    return changes;
  }

  try {
    return JSON.parse(changes) || [];
  } catch (error) {
    console.error("Unable to parse contract activity changes", error);
    return [];
  }
};

const ContractActivitiesHistoryModal = forwardRef((props, ref) => {
  const { schoolId, trainingId, contractId } = props;
  const accessToken = "";
  const [visible, setVisible] = useState(false);

  const activitiesQuery = contractsAPI.GetActivitiesHistory(
    schoolId,
    trainingId,
    accessToken,
    contractId
  );

  const activities = activitiesQuery.data?.data || [];
  const processedActivities = activities.map((activity) => {
    const parsedChanges = parseChanges(activity?.changes);
    return { ...activity, parsedChanges };
  });

  useImperativeHandle(ref, () => ({
    show: () => {
      setVisible(true);
      activitiesQuery.refetch();
    },
    hide: () => setVisible(false),
  }));

  const renderChangeItem = (change, index) => {
    const fieldName = change?.field_name;
    const originalRaw = getDisplayValue(
      fieldName,
      change?.original_value_name,
      change?.original_value
    );
    const nextRaw = getDisplayValue(
      fieldName,
      change?.new_value_name,
      change?.new_value
    );
    const original = formatDisplayValue(fieldName, originalRaw);
    const next = formatDisplayValue(fieldName, nextRaw);
    const paymentDescriptor =
      change?.new_value_name || change?.original_value_name || null;
    const isPaymentDateField =
      fieldName === "payment_date" || fieldName === "payment_received_date";

    return (
      <li key={index}>
        <strong className="mr-2">{getFieldDisplayName(fieldName)}:</strong>
        {isPaymentDateField && paymentDescriptor
          ? `${paymentDescriptor} - ${original} → ${next}`
          : `${original} → ${next}`}
      </li>
    );
  };

  const renderActivityItem = (activity, index) => {
    const changes = activity?.parsedChanges || [];
    const memberFullName = [activity?.member?.firstname, activity?.member?.lastname]
      .filter(Boolean)
      .join(" ")
      .trim();
    const memberDisplayName =
      activity?.member_name || memberFullName || activity?.member_id || "Membre";
    return (
      <div className="col-12" key={index}>
        <div className="mb-2 pb-1 border-bottom-1 border-gray-200">
          <div className="flex align-items-center">
            <MeaMemberAvatar
              member={activity?.member}
              schoolId={schoolId}
              trainingId={trainingId}
            />
            <span className="ml-2">
              a fait une modification le{" "}
              {ToFormatedDateForEvents(new Date(activity?.created_at), false)}
            </span>
          </div>
          <div className="p-card-body">
            {changes.length === 0 ? (
              <div className="text-500">Aucun détail de modification disponible.</div>
            ) : (
              <ul>
                {changes.map((change, idx) => renderChangeItem(change, idx))}
              </ul>
            )}
          </div>
        </div>
      </div>
    );
  };

  const listTemplate = (items) => (
    <div className="grid grid-nogutter">
      {items.map((activity, index) => renderActivityItem(activity, index))}
    </div>
  );

  return (
    <Dialog
      header="Historique des activités"
      visible={visible}
      onHide={() => setVisible(false)}
      style={{ width: "60vw", maxWidth: "800px" }}
      breakpoints={{ "960px": "80vw", "641px": "95vw" }}
    >
      {activitiesQuery.isLoading ? (
        <MeaLoadingContent objectQuery={activitiesQuery} />
      ) : activitiesQuery.isError ? (
        <div className="text-center p-3 text-danger">
          {activitiesQuery.error?.message || "Erreur lors du chargement de l'historique des activités."}
        </div>
      ) : (
        processedActivities.length === 0 ? (
          <div className="p-3 text-center text-500 border-1 border-round-sm surface-border">
            Aucun historique d'activité disponible pour ce contrat.
          </div>
        ) : (
          <DataView value={processedActivities} listTemplate={listTemplate} />
        )
      )}
    </Dialog>
  );
});

export default ContractActivitiesHistoryModal;

